"use client";

import * as React from "react";
import { defaultLocale, isLocale, normalizeLocale, type Locale } from "@/lib/i18n";
import { useVisiblePathname } from "@/components/useVisiblePathname";

function getCookie(name: string): string | null {
  if (typeof document === "undefined") return null;
  const value = `; ${document.cookie}`;
  const parts = value.split(`; ${name}=`);
  if (parts.length < 2) return null;
  return parts.pop()!.split(";").shift() ?? null;
}

export function useCurrentLocale(): Locale {
  const pathname = useVisiblePathname();
  const [locale, setLocale] = React.useState<Locale>(() => {
    // `pathname` is already SSR-safe (useVisiblePathname falls back to
    // Next's real per-request usePathname() on the server), so derive from
    // it directly instead of re-checking `typeof window` here — that used to
    // hardcode `defaultLocale` on the server for every non-en-us route,
    // causing a server/client render mismatch after hydration.
    const first = pathname.split("/").filter(Boolean)[0] ?? "";
    const fromPath = normalizeLocale(first);
    if (fromPath && isLocale(fromPath)) return fromPath;
    if (typeof window !== "undefined") {
      const raw = getCookie("woltrio_locale");
      const fromCookie = raw ? normalizeLocale(raw) : "";
      if (fromCookie && isLocale(fromCookie)) return fromCookie;
    }
    return defaultLocale;
  });

  React.useLayoutEffect(() => {
    const first = pathname.split("/").filter(Boolean)[0] ?? "";
    const fromPath = normalizeLocale(first);
    if (fromPath && isLocale(fromPath)) {
      setLocale(fromPath);
      return;
    }
    const raw = getCookie("woltrio_locale");
    const fromCookie = raw ? normalizeLocale(raw) : "";
    if (fromCookie && isLocale(fromCookie)) {
      setLocale(fromCookie);
      return;
    }
    setLocale(defaultLocale);
  }, [pathname]);

  return locale;
}

